"use client";

import React from "react";
import { Sparkles } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import Reveal from "@/components/common/Reveal";
import { useLanguage } from "@/hooks/useLanguage";
import { pick } from "@/lib/i18n";
interface ContentData {
  eyebrow: string;
  heading: string;
  p1: string;
  p2: string;
  statement: string;
  hutuobadge: string;
}
const CONTENT: Record<"en" | "zh" | "es" | "de" | "fr" | "pt", ContentData> = {
  en: {
    eyebrow: "Our Name",
    heading: 'The Meaning Behind "Mutual"',
    p1: 'The name Mutual is rooted in the core meaning of our Chinese company name, "Hutuo" (互拓).',
    p2: '"Hutuo" is inspired by the Hutuo River (滹沱河) in Hebei, while the characters "互拓" embody our commitment to mutual development, cooperation, and shared growth.',
    statement: "Mutual — cooperation and shared growth.",
    hutuobadge: "Hutuo River Heritage · Hebei"
  },
  zh: {
    eyebrow: "我们的名字",
    heading: '"Mutual"的含义',
    p1: '品牌名 Mutual 源自我们中文公司名“互拓”的核心内涵。',
    p2: '“互拓”的灵感源于河北滹沱河，“互拓”二字体现了互助协作、共同发展与携手共赢的理念。',
    statement: "互拓 — 合作共赢，共同发展。",
    hutuobadge: "滹沱河文脉 · 河北"
  },
  es: {
    eyebrow: "Nuestro Nombre",
    heading: 'El significado detrás de "Mutual"',
    p1: 'El nombre Mutual se fundamenta en el significado esencial de nuestro nombre de empresa en chino, "Hutuo" (互拓).',
    p2: '"Hutuo" se inspira en el río Hutuo (滹沱河) en Hebei, mientras que los caracteres "互拓" representan nuestro compromiso con el desarrollo mutuo, la cooperación y el crecimiento compartido.',
    statement: "Mutual — cooperación y crecimiento compartido.",
    hutuobadge: "Herencia del río Hutuo · Hebei"
  },
  de: {
    eyebrow: "Unser Name",
    heading: 'Die Bedeutung hinter "Mutual"',
    p1: 'Der Name Mutual gründet auf der Kernbedeutung unseres chinesischen Firmennamens „Hutuo“ (互拓).',
    p2: '„Hutuo“ ist vom Fluss Hutuo (滹沱河) in Hebei inspiriert, während die Schriftzeichen „互拓“ unser Engagement für gegenseitige Entwicklung, Zusammenarbeit und gemeinsames Wachstum verkörpern.',
    statement: "Mutual — Zusammenarbeit und gemeinsames Wachstum.",
    hutuobadge: "Hutuo-Fluss-Ursprung · Hebei"
  },
  fr: {
    eyebrow: "Notre Nom",
    heading: 'La signification de « Mutual »',
    p1: 'Le nom Mutual s’enracine dans la signification profonde de notre raison sociale en chinois, « Hutuo » (互拓).',
    p2: '« Hutuo » s’inspire de la rivière Hutuo (滹沱河) dans le Hebei, tandis que les caractères « 互拓 » incarnent notre engagement envers le développement mutuel, la coopération et la croissance partagée.',
    statement: "Mutual — coopération et croissance partagée.",
    hutuobadge: "Origine de la rivière Hutuo · Hebei"
  },
  pt: {
    eyebrow: "Nosso Nome",
    heading: 'O significado por trás de "Mutual"',
    p1: 'O nome Mutual tem suas raízes no significado central da nossa razão social em chinês, "Hutuo" (互拓).',
    p2: '"Hutuo" é inspirado no rio Hutuo (滹沱河), em Hebei, enquanto os caracteres "互拓" expressam nosso compromisso com o desenvolvimento mútuo, a cooperação e o crescimento compartilhado.',
    statement: "Mutual — cooperação e crescimento compartilhado.",
    hutuobadge: "Origem do rio Hutuo · Hebei"
  }
};
export default function BrandMeaning() {
  const {
    lang
  } = useLanguage();
  const current = pick(lang, CONTENT);
  return <section id="brandmeaning" data-nav="light" className="relative bg-muted/40 text-foreground overflow-hidden border-b border-border mf-section">
      {/* Subtle abstract river contour graphic representing Hutuo River */}
      <div className="pointer-events-none absolute inset-0 flex items-center justify-center opacity-40 select-none overflow-hidden" aria-hidden="true">
        <svg viewBox="0 0 1200 400" className="w-[140%] max-w-none text-border/60" fill="none" stroke="currentColor" strokeWidth="1">
          <path d="M0 220 C 300 280, 450 160, 750 210 C 950 240, 1100 170, 1200 190" />
          <path d="M0 250 C 280 300, 470 190, 740 235 C 930 265, 1080 200, 1200 220" />
          <path d="M0 280 C 260 320, 490 220, 730 260 C 910 290, 1060 230, 1200 250" />
        </svg>
      </div>

      <div className="relative mf-container">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-center">
          {/* Left Column (7 cols on lg) */}
          <div className="lg:col-span-7 space-y-5">
            <Reveal>
              {/* Eyebrow badge */}
              <div className="inline-flex items-center gap-2 font-mono mf-caption uppercase tracking-wider text-foreground font-semibold px-2.5 py-1 rounded-md bg-muted border border-border w-fit">
                <Sparkles className="size-3.5 text-primary" />
                <span>{current.eyebrow}</span>
              </div>
            </Reveal>

            {/* Section Heading */}
            <Reveal delay={60}>
              <div className="space-y-3">
                <h2 className="mf-h2 text-foreground font-default">
                  {current.heading}
                </h2>
                <div className="w-12 h-px bg-primary/40" aria-hidden="true" />
              </div>
            </Reveal>

            {/* Narrative Paragraphs */}
            <Reveal delay={120}>
              <div className="space-y-4 max-w-2xl">
                <p className="mf-lead text-muted-foreground leading-relaxed">
                  {current.p1}
                </p>
                <p className="mf-lead text-muted-foreground leading-relaxed">
                  {current.p2}
                </p>
              </div>
            </Reveal>
          </div>

          {/* Right Column: Typographic Hutuo Card (5 cols on lg) */}
          <div className="lg:col-span-5 w-full">
            <Reveal delay={100}>
              <Card className="rounded-lg border border-border bg-card p-8 sm:p-10 flex flex-col items-center text-center mf-hover-lift">
                <div className="mb-3 font-mono mf-caption uppercase tracking-wider text-muted-foreground">
                  {current.hutuobadge}
                </div>

                {/* Cultural/Etymological reference characters */}
                <div lang="zh" className="text-6xl sm:text-7xl font-semibold text-primary tracking-wide select-text leading-tight">
                  互拓
                </div>

                {/* Pinyin Caption */}
                <div className="mt-2 font-mono mf-caption uppercase tracking-widest text-muted-foreground">
                  Hùtuò
                </div>

                <Separator className="my-6 w-16 mx-auto bg-border" />

                {/* Statement */}
                <p className="text-base sm:text-lg font-medium text-foreground tracking-tight max-w-xs">
                  {current.statement}
                </p>
              </Card>
            </Reveal>
          </div>
        </div>
      </div>
    </section>;
}